import { toHex, type CheckStatus, type InkLook, type MockupView, type Placement, type Separation, type SepMode, type Step } from '@eip/engine';
import type { DocSep } from '../state/sep';
import { stepsForGarment, type DocEntry, type Settings } from '../state/store';
import { filmSizeValue } from './sep-ui';

export interface ReportCheck { id: string; label: string; status: CheckStatus; value: number; unit: string; message: string }
export interface ReportSummary { pass: number; warn: number; fail: number; info: number }
/** What the file was before the pipeline touched it; `null` until it has opened. */
export interface ReportSource {
  width: number; height: number; dpi: number;
  /** True when the file carried no density and `dpi` was assumed (JPG without JFIF/EXIF, WebP). */
  dpiAssumed: boolean;
  kind: 'png' | 'psd' | 'bitmap';
  colorProfile: 'srgb' | 'icc' | 'unknown';
}
/** The screen-print half of a job ticket: what goes on press, at what screening,
 *  on what film. `settings` is the whole separation, so a ticket can seed a
 *  reprint; everything else is the flattened, human-readable view of it. */
export interface ReportSeparation {
  mode: SepMode;
  /** Enabled inks in press order, each with the coverage of its plate (0 when the build produced none). */
  inks: Array<{ name: string; hex: string; coverage: number; order: number }>;
  /** Every plate the build produced, underbase and highlight included. */
  plates: Array<{ name: string; coverage: number }>;
  screen: { lpi: number; angle: number; angleMode: string; shape: string; filmDpi: number; mesh: number; indexDpi: number };
  film: { size: string; mirror: boolean; marks: boolean };
  /** Fraction (0..1) of opaque pixels that matched no ink. */
  unmatched: number;
  /** The last build's failure, when it had one: these plates are whatever the
   *  previous successful build left behind, so the ticket has to say so. */
  error?: string;
  settings: Separation;
}

/**
 * One colourway of a garment mockup, as the ticket lists it: which blank, in
 * what colour, with the print at what size — and the file name the export
 * writes it under, so a ticket travelling beside an archive names its contents.
 *
 * Built by `lib/mockups.ts` (which owns the file names and the registry lookup)
 * and handed in; this module deliberately does not reach for it — films.ts
 * already imports this one, and mockups.ts needs films.ts's `slug`.
 */
export interface ReportMockup {
  /** The blank's name, e.g. `Black (flat)`. */
  template: string;
  view: MockupView;
  /** The blank's colour name, or the hex when the garment is being recoloured —
   *  the same caption the colourway sheet prints under this tile. */
  colorName: string;
  hex: string;
  look: InkLook;
  /** What is behind the garment, as `describeBackdrop` says it: `none` (the studio backdrop the
   *  blank was photographed against), a hex, `gradient <top> → <bottom>`, or `scene <name>`. A
   *  string rather than the job's `Backdrop`, because the ticket is read by people and a scene id
   *  is a UUID — and because a scene the shop has since deleted has to read as missing. */
  backdrop: string;
  placement: Placement;
  /** Degrees clockwise about the art rect's centre; 0 for an unturned print. */
  rotationDeg: number;
  /** The job's other prints, in the order they composite. `areaName` is what the blank calls the
   *  area (the id when it has no name of its own); `docName` is the document whose art each one
   *  prints — `null` when it prints THIS document's own art, and null too when the document is no
   *  longer open to be named. Disabled prints are left out: they print nothing. */
  extraPrints: Array<{
    areaId: string; areaName: string; docName: string | null; placement: Placement; rotationDeg: number;
    /** Whether this blank HAS the area the print names (`areaById`), which is what tells the two
     *  reasons a print can be skipped apart: an area that is there but printed nothing had no
     *  design, and one that is not there was retained from another blank. */
    areaOnBlank: boolean;
  }>;
  /** The areas the last render could NOT print (`DocMockup.skipped`): a design that is closed, or
   *  an area this blank does not have. An `extraPrints` entry named here was not made, and the
   *  ticket says so — with which of the two it was — rather than describing a garment nobody can
   *  produce. */
  skipped: string[];
  file: string;
  /** True when the art did not fit the print area and was cropped by it. */
  clipped: boolean;
}

export interface ReportItem {
  file: string; status: 'ok' | 'failed' | 'pending';
  source: ReportSource | null;
  output: { width: number; height: number; dpi: number; widthIn: number; heightIn: number } | null;
  checks: ReportCheck[]; summary: ReportSummary; warnings: string[];
  /** Why this file is `failed`: the pipeline's error, or — for a file that never
   *  opened (`status: 'error'`) — the decode error. One field, both causes: a
   *  consumer only ever needs "what went wrong with this file". */
  runError?: string;
  /** Present only for a document whose separation has been built. */
  separation?: ReportSeparation;
  /** Present only for a document with a mockup on a prepared blank; one entry
   *  per colourway an export would produce. */
  mockups?: ReportMockup[];
}
export interface Report { generatedAt: string; app: 'Easy Image Prep'; settings: Settings; steps: Step[]; items: ReportItem[] }

export function docSummary(checks: ReadonlyArray<{ status: CheckStatus }>): ReportSummary {
  const s: ReportSummary = { pass: 0, warn: 0, fail: 0, info: 0 };
  for (const c of checks) s[c.status]++;
  return s;
}

/** "pass", "2 warn", "1 fail · 2 warn" — fails first, never mentions passes or infos. */
export function summaryLabel(s: ReportSummary): string {
  const parts: string[] = [];
  if (s.fail) parts.push(`${s.fail} fail`);
  if (s.warn) parts.push(`${s.warn} warn`);
  return parts.length ? parts.join(' · ') : 'pass';
}

/** `'13x19'`/`'fit'` when the film size names one of those, else `'14x20'` inches. */
function filmSizeLabel(size: Separation['film']['size']): string {
  const named = filmSizeValue(size);
  if (named !== 'custom') return named;
  return size === 'fit' ? 'fit' : `${size.widthIn}x${size.heightIn}`;
}

/** The separation section, or `undefined` when this doc has no BUILT separation
 *  — a ticket that listed inks for plates nobody has generated would describe a
 *  job that cannot be printed. */
export function reportSeparation(sep: DocSep | undefined): ReportSeparation | undefined {
  if (!sep || sep.plates.length === 0) return undefined;
  const s = sep.separation;
  const coverageByInk = new Map(sep.plates.filter((p) => p.inkId).map((p) => [p.inkId as string, p.coverage]));
  const inks = s.inks
    .filter((ink) => ink.enabled)
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((ink) => ({ name: ink.name, hex: toHex(ink.rgb), coverage: coverageByInk.get(ink.id) ?? 0, order: ink.order }));
  return {
    mode: s.mode,
    inks,
    plates: sep.plates.map((p) => ({ name: p.name, coverage: p.coverage })),
    screen: { lpi: s.screen.lpi, angle: s.screen.angle, angleMode: s.screen.angleMode, shape: s.screen.shape, filmDpi: s.screen.filmDpi, mesh: s.screen.mesh, indexDpi: s.screen.indexDpi },
    film: { size: filmSizeLabel(s.film.size), mirror: s.film.mirror, marks: s.film.marks },
    unmatched: sep.unmatched,
    ...(sep.error ? { error: sep.error } : {}),
    settings: s,
  };
}

export function reportItem(doc: DocEntry, mockups?: ReportMockup[]): ReportItem {
  const checks = doc.checks.map(({ hasOverlay: _h, ...c }) => c);
  const run = doc.run;
  const meta = doc.meta;
  // A file that never opened (`status: 'error'`) is a failure of this job too,
  // and the reason is in `doc.error` rather than `doc.runError` — reporting it
  // as "pending" with no explanation hid the one thing the operator needed.
  const failed = doc.status === 'error' || !!doc.runError;
  const error = doc.runError ?? (doc.status === 'error' ? doc.error : undefined);
  const separation = reportSeparation(doc.sep);
  return {
    file: doc.name,
    status: failed ? 'failed' : run ? 'ok' : 'pending',
    source: meta
      ? { width: meta.width, height: meta.height, dpi: meta.dpi, dpiAssumed: !!meta.dpiAssumed, kind: meta.kind, colorProfile: meta.colorProfile }
      : null,
    output: run ? { width: run.width, height: run.height, dpi: run.dpi, widthIn: run.width / run.dpi, heightIn: run.height / run.dpi } : null,
    checks, summary: docSummary(checks), warnings: run?.warnings ?? [],
    ...(error ? { runError: error } : {}),
    ...(separation ? { separation } : {}),
    ...(mockups && mockups.length ? { mockups } : {}),
  };
}

/**
 * `mockupsFor` is how the mockup section gets in: a callback rather than an
 * import, so this module stays free of the mockup registry (see `ReportMockup`).
 * Left out — every DTF and screen-print caller — and no ticket mentions mockups.
 */
export function buildReport(
  docs: ReadonlyArray<DocEntry>, steps: Step[], settings: Settings, now = new Date(),
  mockupsFor?: (doc: DocEntry) => ReportMockup[] | undefined,
): Report {
  // The steps as the RUNNER executed them: `runNow` applies `stepsForGarment`,
  // so the stored knockout step still carries the persisted/default garment.
  // Printing that produced a ticket whose header said one garment and whose
  // pipeline listing said another, and a JSON `steps` array that would replay
  // the wrong knockout.
  let applied = steps;
  try { applied = stepsForGarment(steps, settings.garmentHex); } catch { /* unparseable hex: print the stored steps rather than nothing */ }
  return { generatedAt: now.toISOString(), app: 'Easy Image Prep', settings, steps: applied, items: docs.map((d) => reportItem(d, mockupsFor?.(d))) };
}

export function reportJson(report: Report): string { return JSON.stringify(report, null, 2); }
